"use client";

import { create } from "zustand";
import { useCallback } from "react";
import { useAuthStore } from "@/lib/auth-store";

interface AuthPromptState {
  isOpen: boolean;
  onAuthenticated?: () => void;
  open: (onAuthenticated?: () => void) => void;
  close: () => void;
}

export const useAuthPromptStore = create<AuthPromptState>((set) => ({
  isOpen: false,
  onAuthenticated: undefined,
  open: (onAuthenticated) => set({ isOpen: true, onAuthenticated }),
  close: () => set({ isOpen: false, onAuthenticated: undefined }),
}));

/**
 * Returns a function that gates a protected action behind authentication.
 * If the user is authenticated, returns `true` (proceed).
 * If anonymous, opens the inline auth dialog and returns `false` (block).
 *
 * Usage:
 * ```ts
 * const requireAuth = useRequireAuth();
 *
 * function handleProtectedAction() {
 *   if (!requireAuth()) return;
 *   // ... proceed with action
 * }
 * ```
 */
export function useRequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const openPrompt = useAuthPromptStore((s) => s.open);

  return useCallback(
    (onAuthenticated?: () => void) => {
      if (isAuthenticated) return true;
      openPrompt(onAuthenticated);
      return false;
    },
    [isAuthenticated, openPrompt],
  );
}
